import { gql } from '@apollo/client';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconChevronUp from '@apollo/icons/default/IconChevronUp.svg';
import IconClose from '@apollo/icons/default/IconClose.svg';
import IconLink from '@apollo/icons/default/IconLink.svg';
import { IconButton, Link, List, ListItem } from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import { parse } from 'graphql';
import _, { groupBy, orderBy, partition } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import {
  Link as ReactRouterLink,
  useHistory,
  useLocation,
} from 'react-router-dom';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { FlatDiffItemFragment } from 'src/app/graph/proposalLayout/diffHelpers/diffHelpers';
import { ProposalStatusBadge } from 'src/app/graph/proposalLayout/ProposalStatusBadge';
import { proposalHomepageRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { InlineGraphQLDiffView } from 'src/components/graphQLDiffView/InlineGraphQLDiffView';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { expandedPrint } from 'src/lib/expandedPrint';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { parseGraphqlMergedDefinitions } from 'src/lib/parseGraphqlMergedDefinitions';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { useBaseAndProposedSchemas } from '../useBaseAndProposedSchemas';

import { MatchingProposalsFeedbackAdmonition } from './MatchingProposalsFeedbackAdmonition';
import {
  getASTNodeFromCoordinate,
  getDiffItemsByLineNumber,
  getFullyRemovedCoordinateNames,
  isAddedOrChangedDiffItem,
  isRemovedDiffItem,
  parseCoordinateFromString,
} from './proposalCheckHelpers';

export const MatchingProposalsDrawerFragment = gql`
  fragment MatchingProposalsDrawerFragment on ProposalsCheckTask {
    id
    workflow {
      id
      baseSubgraphs {
        hash
        name
      }
      proposedSubgraphs {
        hash
        name
      }
    }
    diffs {
      diffItem {
        type
        ...FlatDiffItemFragment
      }
      hasApprovedProposal
      relatedProposalResults {
        proposal {
          id
        }
      }
      subgraph
    }
    relatedProposalResults {
      statusAtCheck
      proposal {
        id
        displayName
        description
        backingVariant {
          id
          name
        }
      }
    }
  }
  ${FlatDiffItemFragment}
`;

type SingleDiffItem = ReturnType<
  typeof getDiffItemsByLineNumber
>[number][1][number];
type MergedDiffItem = Omit<
  SingleDiffItem,
  '__typename' | 'diffItem' | 'relatedProposalResults'
> & {
  diffItems: Pick<
    SingleDiffItem,
    '__typename' | 'diffItem' | 'relatedProposalResults'
  >[];
};

function getHumanReadableFlatDiffType(diffType: string) {
  return diffType
    .replace('FlatDiff', '')
    .split(/(?=[A-Z])/)
    .join(' ');
}

export const MatchingProposalsDrawer = ({
  className,
  workflowTask,
}: {
  className?: string;
  workflowTask: GraphQLTypes.MatchingProposalsDrawerFragment;
}) => {
  const history = useHistory();
  const { currentProposalCheckDetailsRoute } = useInternalGraphLinking();
  const { linkedSubgraphName, linkedCoordinateName, isDescription } =
    useRouteParams(currentProposalCheckDetailsRoute);
  const canAccessProposals = useCapabilities().capabilities?.proposals;
  const isOpen =
    canAccessProposals && !!linkedSubgraphName && !!linkedCoordinateName;

  const handleCopy = useCallback(() => copy(window.location.href), []);

  const location = useLocation();

  const currentSubgraphHashes = useMemo(() => {
    return {
      baseSchemaHash: workflowTask.workflow.baseSubgraphs?.find(
        (subgraph) => subgraph.name === linkedSubgraphName,
      )?.hash,
      proposedSchemaHash: workflowTask.workflow.proposedSubgraphs?.find(
        (subgraph) => subgraph.name === linkedSubgraphName,
      )?.hash,
    };
  }, [
    linkedSubgraphName,
    workflowTask.workflow.baseSubgraphs,
    workflowTask.workflow.proposedSubgraphs,
  ]);

  const { baseSchema, proposedSchema } = useBaseAndProposedSchemas(
    currentSubgraphHashes,
  );

  const schemas = useMemo(() => {
    const parseSdl = (sdl: string | undefined) => {
      return sdl
        ? parseGraphqlMergedDefinitions(expandedPrint(parse(sdl)))
        : null;
    };
    return {
      base: parseSdl(baseSchema),
      proposed: parseSdl(proposedSchema),
    };
  }, [baseSchema, proposedSchema]);

  const fullyRemovedCoordinateNames = useMemo(() => {
    return getFullyRemovedCoordinateNames(workflowTask.diffs);
  }, [workflowTask.diffs]);

  const diffItemsByLineNumber = useMemo(() => {
    const baseDiffItems = baseSchema
      ? getDiffItemsByLineNumber(
          workflowTask.diffs.filter(
            (diffItem) =>
              diffItem.subgraph === linkedSubgraphName &&
              isRemovedDiffItem(diffItem.diffItem, fullyRemovedCoordinateNames),
          ),
          parse(baseSchema),
        )
      : undefined;
    const proposedDiffItems = proposedSchema
      ? getDiffItemsByLineNumber(
          workflowTask.diffs.filter(
            (diffItem) =>
              diffItem.subgraph === linkedSubgraphName &&
              isAddedOrChangedDiffItem(
                diffItem.diffItem,
                fullyRemovedCoordinateNames,
              ),
          ),
          parse(proposedSchema),
        )
      : undefined;

    return {
      base: baseDiffItems,
      proposed: proposedDiffItems,
    };
  }, [
    baseSchema,
    fullyRemovedCoordinateNames,
    linkedSubgraphName,
    proposedSchema,
    workflowTask.diffs,
  ]);

  const diffItemsAtCurrentLinkedCoordinateLineNumber = useMemo(() => {
    const baseLineNumberAndValues = (diffItemsByLineNumber.base ?? []).find(
      ([, diffItems]) => {
        return diffItems.some((diffItem) => {
          return (
            diffItem.linkableCoordinateName === linkedCoordinateName &&
            diffItem.isDescription === isDescription
          );
        });
      },
    );
    const proposedLineNumberAndValues = (
      diffItemsByLineNumber.proposed ?? []
    ).find(([, diffItems]) => {
      return diffItems.some((diffItem) => {
        return (
          diffItem.linkableCoordinateName === linkedCoordinateName &&
          diffItem.isDescription === isDescription
        );
      });
    });

    return {
      base: baseLineNumberAndValues
        ? {
            lineNumber: Number(baseLineNumberAndValues[0]),
            diffItems: baseLineNumberAndValues[1],
          }
        : undefined,
      proposed: proposedLineNumberAndValues
        ? {
            lineNumber: Number(proposedLineNumberAndValues[0]),
            diffItems: proposedLineNumberAndValues[1],
          }
        : undefined,
    };
  }, [
    diffItemsByLineNumber.base,
    diffItemsByLineNumber.proposed,
    isDescription,
    linkedCoordinateName,
  ]);

  const [
    currentMergedDiffItemsWithApprovals,
    currentMergedDiffItemsWithoutApprovals,
  ] = useMemo(() => {
    return partition(
      [
        ...(diffItemsAtCurrentLinkedCoordinateLineNumber.base?.diffItems ?? []),
        ...(diffItemsAtCurrentLinkedCoordinateLineNumber.proposed?.diffItems ??
          []),
      ],
      (diffItem) => diffItem.hasApprovedProposal,
    ).map((diffItems) => {
      // Merge diff items with the same coordinate name
      return orderBy(
        diffItems.reduce<MergedDiffItem[]>(
          (
            acc,
            {
              __typename,
              diffItem,
              relatedProposalResults,
              ...currentDiffCoordinate
            },
          ) => {
            const existingDiffItemIndex = acc.findIndex(
              (item) =>
                item.linkableCoordinateName ===
                currentDiffCoordinate.linkableCoordinateName,
            );

            if (!acc[existingDiffItemIndex]) {
              acc.push({
                ...currentDiffCoordinate,
                diffItems: [
                  {
                    __typename,
                    diffItem,
                    relatedProposalResults,
                  },
                ],
              });
            } else {
              acc[existingDiffItemIndex]?.diffItems.push({
                __typename,
                diffItem,
                relatedProposalResults,
              });
            }

            return acc;
          },
          [],
        ),
        [(diffItem) => diffItem.linkableCoordinateName],
      );
    });
  }, [diffItemsAtCurrentLinkedCoordinateLineNumber]);

  const currentCoordinatePrinted = useMemo(() => {
    if (!linkedCoordinateName) return undefined;
    const parsedCoordinate = parseCoordinateFromString(linkedCoordinateName);

    const baseCoordinateNode = getASTNodeFromCoordinate(
      parsedCoordinate,
      schemas.base,
    );
    const proposedCoordinateNode = getASTNodeFromCoordinate(
      parsedCoordinate,
      schemas.proposed,
    );

    return {
      baseCoordinate: baseCoordinateNode
        ? expandedPrint(baseCoordinateNode)
        : undefined,
      proposedCoordinate: proposedCoordinateNode
        ? expandedPrint(proposedCoordinateNode)
        : undefined,
    };
  }, [linkedCoordinateName, schemas.base, schemas.proposed]);

  const uniqueLinkableCoordinates = useMemo(() => {
    const baseLineNumberAndValues = diffItemsByLineNumber.base?.flatMap<
      [
        number,
        {
          linkableCoordinateName: string;
          subgraph: string;
          isDescription: boolean;
          version: 'base';
        },
      ]
    >(([lineNumber, diffItems]) => {
      if (isMinLengthArray(1, diffItems)) {
        return [
          [
            parseInt(lineNumber, 10),
            {
              linkableCoordinateName: diffItems[0].linkableCoordinateName,
              subgraph: diffItems[0].subgraph,
              isDescription: diffItems[0].isDescription,
              version: 'base',
            },
          ],
        ];
      }
      return [];
    });

    const proposedLineNumberAndValues = diffItemsByLineNumber.proposed?.flatMap<
      [
        number,
        {
          linkableCoordinateName: string;
          subgraph: string;
          isDescription: boolean;
          version: 'proposed';
        },
      ]
    >(([lineNumber, diffItems]) => {
      if (isMinLengthArray(1, diffItems)) {
        return [
          [
            parseInt(lineNumber, 10),
            {
              linkableCoordinateName: diffItems[0].linkableCoordinateName,
              subgraph: diffItems[0].subgraph,
              isDescription: diffItems[0].isDescription,
              version: 'proposed',
            },
          ],
        ];
      }
      return [];
    });

    return _.uniqBy(
      [
        ...(baseLineNumberAndValues ?? []),
        ...(proposedLineNumberAndValues ?? []),
      ],
      ([
        __,
        {
          linkableCoordinateName,
          subgraph,
          isDescription: isDiffItemDescription,
        },
      ]) =>
        `${subgraph}.${linkableCoordinateName}${
          isDiffItemDescription ? '~description' : ''
        }`,
    );
  }, [diffItemsByLineNumber.base, diffItemsByLineNumber.proposed]);

  const currentCoordinateIndex = useMemo(() => {
    return uniqueLinkableCoordinates.findIndex(([lineNumber, coordinate]) => {
      return (
        (lineNumber ===
          diffItemsAtCurrentLinkedCoordinateLineNumber.base?.lineNumber &&
          coordinate.version === 'base') ||
        (lineNumber ===
          diffItemsAtCurrentLinkedCoordinateLineNumber.proposed?.lineNumber &&
          coordinate.version === 'proposed')
      );
    });
  }, [
    diffItemsAtCurrentLinkedCoordinateLineNumber.base?.lineNumber,
    diffItemsAtCurrentLinkedCoordinateLineNumber.proposed?.lineNumber,
    uniqueLinkableCoordinates,
  ]);

  return (
    <div
      className={classnames(
        'z-30 flex h-full w-[26rem] flex-col border-l border-l-silver-dark bg-white 4xl:w-[37rem]',
        !isOpen && 'hidden',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-b-silver-dark px-1 py-2">
        <div className="space-x-2">
          <IconButton
            aria-label="Previous Coordinate"
            isDisabled={currentCoordinateIndex <= 0}
            variant="hidden"
            as={ReactRouterLink}
            to={currentProposalCheckDetailsRoute.locationFrom({
              location,
              patch: {
                linkedSubgraphName:
                  uniqueLinkableCoordinates[currentCoordinateIndex - 1]?.[1]
                    ?.subgraph,
                linkedCoordinateName:
                  uniqueLinkableCoordinates[currentCoordinateIndex - 1]?.[1]
                    ?.linkableCoordinateName,
                isDescription:
                  uniqueLinkableCoordinates[currentCoordinateIndex - 1]?.[1]
                    ?.isDescription,
              },
            })}
          >
            <IconChevronLeft />
          </IconButton>
          <IconButton
            aria-label="Next Coordinate"
            isDisabled={
              currentCoordinateIndex === uniqueLinkableCoordinates.length - 1
            }
            variant="hidden"
            as={ReactRouterLink}
            to={currentProposalCheckDetailsRoute.locationFrom({
              location,
              patch: {
                linkedSubgraphName:
                  uniqueLinkableCoordinates[currentCoordinateIndex + 1]?.[1]
                    ?.subgraph,
                linkedCoordinateName:
                  uniqueLinkableCoordinates[currentCoordinateIndex + 1]?.[1]
                    ?.linkableCoordinateName,
                isDescription:
                  uniqueLinkableCoordinates[currentCoordinateIndex + 1]?.[1]
                    ?.isDescription,
              },
            })}
          >
            <IconChevronRight />
          </IconButton>
        </div>
        <div className="flex items-center space-x-2">
          <Tooltip label="Copy link" clickedLabel="Copied" placement="top">
            <IconButton
              variant="hidden"
              aria-label="Copy link"
              onClick={handleCopy}
            >
              <IconLink />
            </IconButton>
          </Tooltip>
          <IconButton
            variant="hidden"
            aria-label="Close drawer"
            onClick={() => {
              history.push(
                currentProposalCheckDetailsRoute.locationFrom({
                  location,
                  patch: {
                    linkedSubgraphName: RouteConfig.DELETE,
                    linkedCoordinateName: RouteConfig.DELETE,
                  },
                }),
              );
            }}
          >
            <IconClose />
          </IconButton>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col space-y-6 p-6">
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto">
          <InlineGraphQLDiffView
            oldSdl={currentCoordinatePrinted?.baseCoordinate}
            newSdl={currentCoordinatePrinted?.proposedCoordinate}
            lineNumberClassName="hidden"
            className="max-h-60 overflow-auto rounded bg-secondary p-2"
          />
          {isMinLengthArray(1, currentMergedDiffItemsWithoutApprovals) && (
            <CollapsibleCoordinatesSection
              title="Unapproved Changes"
              defaultOpen
            >
              <div className="divide-y divide-primary">
                {currentMergedDiffItemsWithoutApprovals.map(
                  (mergedDiffItem, i) => (
                    <DiffItemWithMatchingProposals
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${mergedDiffItem.linkableCoordinateName}_${mergedDiffItem.isDescription}_${mergedDiffItem.subgraph}_${i}`}
                      mergedDiffItem={mergedDiffItem}
                      workflowTask={workflowTask}
                    />
                  ),
                )}
              </div>
            </CollapsibleCoordinatesSection>
          )}
          {isMinLengthArray(1, currentMergedDiffItemsWithApprovals) && (
            <CollapsibleCoordinatesSection
              title="Approved Changes"
              defaultOpen={
                !currentMergedDiffItemsWithoutApprovals ||
                currentMergedDiffItemsWithoutApprovals.length === 0
              }
            >
              <div className="divide-y divide-primary">
                {currentMergedDiffItemsWithApprovals.map(
                  (mergedDiffItem, i) => (
                    <DiffItemWithMatchingProposals
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${mergedDiffItem.linkableCoordinateName}_${mergedDiffItem.isDescription}_${mergedDiffItem.subgraph}_${i}`}
                      mergedDiffItem={mergedDiffItem}
                      workflowTask={workflowTask}
                    />
                  ),
                )}
              </div>
            </CollapsibleCoordinatesSection>
          )}
        </div>

        <MatchingProposalsFeedbackAdmonition />
      </div>
    </div>
  );
};

const DiffItemWithMatchingProposals = ({
  mergedDiffItem,
  workflowTask,
}: {
  mergedDiffItem: MergedDiffItem;
  workflowTask: GraphQLTypes.MatchingProposalsDrawerFragment;
}) => {
  const shouldDescriptionsWarn = useLDFlag(
    'gov-proposal-checks-descriptions-warn',
  );

  const diffItemsByDiffType = useMemo(() => {
    return orderBy(
      Object.entries(
        groupBy(
          mergedDiffItem.diffItems,
          (diffItem) => diffItem.diffItem.__typename,
        ),
      ),
      ([diffType]) => diffType,
    );
  }, [mergedDiffItem.diffItems]);

  return (
    <div>
      <div className="mt-4 overflow-x-auto rounded bg-secondary p-4">
        <div
          className="border-l-2 border-primary pl-3 font-mono text-sm"
          title={mergedDiffItem.linkableCoordinateName}
        >
          {mergedDiffItem.linkableCoordinateName}
          {mergedDiffItem.isDescription && ' (description)'}
        </div>
      </div>
      <div className="flex flex-col divide-y divide-primary">
        {diffItemsByDiffType.map(([diffType, diffItems]) => (
          <div
            key={`${diffType}_${mergedDiffItem.linkableCoordinateName}`}
            className="py-4"
          >
            <h4 className="text-base font-semibold">
              {getHumanReadableFlatDiffType(diffType)} ({diffItems.length}{' '}
              {format.pluralize(diffItems.length, 'instance')})
            </h4>
            {shouldDescriptionsWarn &&
              diffType === 'FlatDiffChangeDescription' && (
                <div className="mt-2">
                  <p className="text-sm italic text-secondary">
                    No matching description was found in an approved proposal.
                  </p>
                </div>
              )}

            {diffItems.map((diffItem, i) => {
              const currentMatchingProposals =
                diffItem.relatedProposalResults?.flatMap((proposal) => {
                  const matchingProposal =
                    workflowTask.relatedProposalResults.find(
                      (relatedProposal) =>
                        relatedProposal.proposal.id === proposal.proposal.id,
                    );
                  return matchingProposal ?? [];
                });
              return (
                <div
                  key={`${mergedDiffItem.linkableCoordinateName}_${diffItem.diffItem.type}`}
                  className={classnames(i > 0 ? 'mt-4' : 'mt-2')}
                >
                  <h5 className="mb-1 text-sm font-semibold">
                    Matching{' '}
                    {format.pluralize(
                      currentMatchingProposals.length,
                      'Proposal',
                    )}{' '}
                    ({currentMatchingProposals.length})
                  </h5>
                  {!mergedDiffItem.hasApprovedProposal &&
                    (isMinLengthArray(1, currentMatchingProposals) ? (
                      <p className="text-sm text-secondary">
                        Not all changes are in approved proposals.
                      </p>
                    ) : (
                      <p className="text-sm text-secondary">
                        No matching proposals were found for this change.
                      </p>
                    ))}
                  <List>
                    {currentMatchingProposals?.map((proposal) => (
                      <ListItem key={proposal.proposal.id} className="pl-2">
                        <MatchingProposal proposal={proposal} />
                      </ListItem>
                    ))}
                  </List>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const MatchingProposal = ({
  proposal,
}: {
  proposal: GraphQLTypes.MatchingProposalsDrawerFragment['relatedProposalResults'][number];
}) => {
  const { currentVariantRouteConfig } = useInternalGraphLinking();
  const location = useLocation();

  const proposalVariant = proposal.proposal.backingVariant;

  return (
    <div className="flex w-full items-center justify-between">
      <Link
        to={proposalHomepageRouteConfig.locationFrom({
          location,
          patch: {
            graphVariant: proposalVariant.name,
          },
          fromRouteConfig: currentVariantRouteConfig,
        })}
        className="truncate text-sm font-semibold text-link"
      >
        {proposal.proposal.displayName}
      </Link>
      <ProposalStatusBadge status={proposal.statusAtCheck} className="ml-4" />
    </div>
  );
};

const CollapsibleCoordinatesSection = ({
  title,
  children,
  defaultOpen,
  className,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className={className}>
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <h3 className="text-lg font-semibold">{title}</h3>
        <IconButton
          aria-label="Toggle section"
          variant="hidden"
          icon={isOpen ? <IconChevronUp /> : <IconChevronDown />}
        />
      </div>
      {isOpen && children}
    </div>
  );
};
